import { z } from "zod";

const normalizeCarrier = (arr) =>
  (Array.isArray(arr) ? arr : []).map((s) => String(s ?? ""));

const emptyToUndef = (v) =>
  typeof v === "string" && v.trim() === "" ? undefined : v;

export const Step4Schema = z
  .object({
    // Mirror of step1.carrier; always keep in sync from the UI
    carrier: z
      .array(z.enum(["Cover Whale", "Progressive", "Nirvana", "Accelerant"]))
      .default([]),

    // Legacy Nirvana-only fields (kept for backward compatibility and Nirvana-only flows)
    // Optional by default; required when Nirvana-only (or CW+Nirvana where Nirvana remains on the quote)
    telemetryOption: z.preprocess(
      (v) => (v === "" ? undefined : v),
      z.enum(["eld_only", "dash_cam_dual_ai_connected"], { required_error: "Select a telemetry option" }).optional()
    ),

    // Optional by default; required when Nirvana is selected
    telemetryProvider: z.preprocess(
      emptyToUndef,
      z.string({ required_error: "Telemetry provider is required" }).min(1, "Telemetry provider is required").optional()
    ),

    // New richer field model used primarily for Cover Whale-only and CW+Nirvana flows
    hasEld: z.preprocess(
      (v) => {
        if (v === "yes") return true;
        if (v === "no") return false;
        return v;
      },
      z.boolean().optional()
    ),
    eldProviderKey: z.preprocess(
      emptyToUndef,
      z.string().optional()
    ),
    eldProviderOtherText: z.preprocess(
      emptyToUndef,
      z.string().optional()
    ),
    hasDashCam: z.preprocess(
      (v) => {
        if (v === "yes") return true;
        if (v === "no") return false;
        return v;
      },
      z.boolean().optional()
    ),
    dashCamProviderText: z.preprocess(
      emptyToUndef,
      z.string().optional()
    ),
  })
  .superRefine((data, ctx) => {
    const carriers = normalizeCarrier(data.carrier);
    const hasCW = carriers.includes("Cover Whale");
    const hasNir = carriers.includes("Nirvana");
    const cwOnly = hasCW && !hasNir;
    const nirOnly = hasNir && !hasCW;
    const cwAndNir = hasCW && hasNir;

    // --- Nirvana-only flow ---
    if (nirOnly && !data.telemetryOption) {
      ctx.addIssue({
        code: "custom",
        path: ["telemetryOption"],
        message: "Select a telemetry option.",
      });
    }

    if (
      nirOnly &&
      (!data.telemetryProvider || data.telemetryProvider.trim().length === 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["telemetryProvider"],
        message: "Who is the insured ELD/Dash Camera Provider? (required for Nirvana).",
      });
    }

    // --- Cover Whale-only flow ---
    if (cwOnly && !data.telemetryOption) {
      ctx.addIssue({
        code: "custom",
        path: ["telemetryOption"],
        message: "Select a telemetry option.",
      });
    }

    // --- Cover Whale + Nirvana combined flow ---
    if (cwAndNir) {
      if (!data.telemetryOption) {
        ctx.addIssue({
          code: "custom",
          path: ["telemetryOption"],
          message: "Select a telemetry option.",
        });
      }

      // Nirvana still requires a provider text in the combined flow
      if (!data.telemetryProvider || data.telemetryProvider.trim().length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["telemetryProvider"],
          message: "Who is the insured ELD/Dash Camera Provider? (required for Nirvana).",
        });
      }
    }
  });
